import { ASSET_LIMITS } from './asset';
/**
 * Floor plans and placements (design 2026-10-29, Teilprojekt B).
 *
 * A plan is an image (PNG or JPEG) attached to any location node. Measurement
 * points and beacons are placed on it with RELATIVE coordinates (0..1 from the
 * top left), so a replaced image of another resolution keeps every marker.
 * Each placement is its own document (plan x object), not a list inside the
 * plan and not a field on the object: two people placing different points on
 * the same plan never collide.
 */
export const PLAN_IMAGE_MEDIA_TYPES = ['image/png', 'image/jpeg'];
export const FLOOR_PLAN_LIMITS = {
    maxImageBytes: ASSET_LIMITS.maxImageBytes,
    /** Per side. Larger scans are not usable in a browser anyway. */
    maxDimension: 20_000,
    maxNameLength: 100,
    /** A replacement whose aspect ratio differs by MORE than this warns. */
    aspectTolerancePercent: 2,
    /** Per side. The browser renders thumbnails at 480; a little headroom. */
    thumbnailMaxDimension: 512,
    thumbnailMaxBytes: 200 * 1024,
};
export const PLACEMENT_REF_TYPES = ['measurementPoint', 'beacon'];
export function placementId(planId, refType, refId) {
    return `plc_${planId}_${refType}_${refId}`;
}
export function isImageInfoFailure(result) {
    return 'error' in result;
}
const NOT_AN_IMAGE = 'Die Datei ist kein PNG- oder JPEG-Bild.';
const TRUNCATED = 'Die Bilddatei ist unvollständig.';
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
function byteAt(bytes, offset) {
    return bytes[offset] ?? 0;
}
function u16(bytes, offset) {
    return byteAt(bytes, offset) * 0x100 + byteAt(bytes, offset + 1);
}
function u32(bytes, offset) {
    return u16(bytes, offset) * 0x10000 + u16(bytes, offset + 2);
}
function readPng(bytes) {
    // Signature (8), chunk length (4), chunk type (4), width (4), height (4).
    if (bytes.length < 24)
        return { error: TRUNCATED };
    const type = String.fromCharCode(byteAt(bytes, 12), byteAt(bytes, 13), byteAt(bytes, 14), byteAt(bytes, 15));
    // The PNG specification requires IHDR to be the first chunk.
    if (type !== 'IHDR')
        return { error: NOT_AN_IMAGE };
    return { mediaType: 'image/png', width: u32(bytes, 16), height: u32(bytes, 20) };
}
/** SOF0-SOF15, except DHT (C4), JPG (C8) and DAC (CC), which share the range. */
function isStartOfFrame(marker) {
    return marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
}
/**
 * Walks the marker segments by their length fields, so 0xFF bytes inside an
 * EXIF/APP block are skipped instead of being read as markers. Stops at the
 * first frame header; baseline and progressive JPEGs differ only in its marker.
 */
function readJpeg(bytes) {
    let offset = 2;
    while (offset < bytes.length) {
        if (byteAt(bytes, offset) !== 0xff)
            return { error: NOT_AN_IMAGE };
        // Any number of 0xFF fill bytes may precede a marker.
        while (offset < bytes.length && byteAt(bytes, offset) === 0xff)
            offset += 1;
        if (offset >= bytes.length)
            break;
        const marker = byteAt(bytes, offset);
        offset += 1;
        if (marker === 0x00)
            return { error: NOT_AN_IMAGE };
        // Standalone markers without a length: TEM and RST0-RST7.
        if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7))
            continue;
        // End of image or start of scan before any frame header: no size to read.
        if (marker === 0xd9 || marker === 0xda)
            return { error: NOT_AN_IMAGE };
        if (offset + 2 > bytes.length)
            break;
        const length = u16(bytes, offset);
        if (length < 2)
            return { error: NOT_AN_IMAGE };
        if (isStartOfFrame(marker)) {
            // length (2), precision (1), height (2), width (2)
            if (offset + 7 > bytes.length)
                break;
            return {
                mediaType: 'image/jpeg',
                height: u16(bytes, offset + 3),
                width: u16(bytes, offset + 5),
            };
        }
        offset += length;
    }
    return { error: TRUNCATED };
}
/**
 * Format and pixel size from the first bytes - never from the file name or the
 * declared content type, both of which the client controls.
 */
export function readImageInfo(bytes) {
    if (bytes.length >= PNG_SIGNATURE.length &&
        PNG_SIGNATURE.every((value, i) => bytes[i] === value)) {
        return readPng(bytes);
    }
    if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
        return readJpeg(bytes);
    }
    return { error: NOT_AN_IMAGE };
}
/** German reason when the size is unusable, `undefined` when it is fine. */
export function checkPlanImageSize(size) {
    if (size.width < 1 || size.height < 1)
        return 'Das Bild hat keine gültige Größe.';
    if (size.width > FLOOR_PLAN_LIMITS.maxDimension || size.height > FLOOR_PLAN_LIMITS.maxDimension) {
        return 'Das Bild ist zu groß (höchstens 20 000 Pixel je Seite).';
    }
    return undefined;
}
/**
 * Whether a replacement image distorts relative positions noticeably.
 * Cross-multiplied in integers: no floating point error right at the limit.
 */
export function aspectRatioChanged(previous, next) {
    const deviation = Math.abs(next.width * previous.height - previous.width * next.height);
    return deviation * 100 > FLOOR_PLAN_LIMITS.aspectTolerancePercent * previous.width * next.height;
}
// --- thumbnail -------------------------------------------------------------
/** Longest side becomes `target`; never upscales; the short side keeps >= 1 px. */
export function thumbnailSize(source, target = 480) {
    const longest = Math.max(source.width, source.height);
    if (longest <= target)
        return { width: source.width, height: source.height };
    const scale = target / longest;
    return {
        width: Math.max(1, Math.round(source.width * scale)),
        height: Math.max(1, Math.round(source.height * scale)),
    };
}
/**
 * German reason when `thumb` is not a usable preview of `image`, `undefined`
 * when it is. The ratio may deviate within the replacement tolerance, or by
 * one pixel of rounding (a very flat plan has a short side of 1-3 pixels,
 * where any tolerance in percent is meaningless).
 */
export function checkPlanThumbnail(image, thumb) {
    if (thumb.width < 1 || thumb.height < 1)
        return 'Das Vorschaubild hat keine gültige Größe.';
    const max = FLOOR_PLAN_LIMITS.thumbnailMaxDimension;
    if (thumb.width > max || thumb.height > max) {
        return 'Das Vorschaubild ist zu groß (höchstens 512 Pixel je Seite).';
    }
    if (!aspectRatioChanged(image, thumb))
        return undefined;
    const expected = thumbnailSize(image, Math.max(thumb.width, thumb.height));
    if (Math.abs(expected.width - thumb.width) <= 1 &&
        Math.abs(expected.height - thumb.height) <= 1) {
        return undefined;
    }
    return 'Das Vorschaubild passt nicht zum Seitenverhältnis des Plans.';
}
/**
 * Objects offered for a plan: everything at or below the plan's location, plus
 * objects without any location. (Placing a measurement point without a location
 * gives it the plan's location; beacons keep none - that is the caller's job.)
 */
export function placementCandidates(items, planLocationId) {
    return items.filter((item) => !item.locationId || item.locationPath.includes(planLocationId));
}
/** Clamps to 0..1; NaN becomes 0 so a broken input never reaches storage. */
export function clampUnit(value) {
    if (Number.isNaN(value))
        return 0;
    return Math.min(1, Math.max(0, value));
}
export function toRelative(pixel, size) {
    return { x: clampUnit(pixel.x / size.width), y: clampUnit(pixel.y / size.height) };
}
export function toPixels(relative, size) {
    return { x: clampUnit(relative.x) * size.width, y: clampUnit(relative.y) * size.height };
}
//# sourceMappingURL=floor-plan.js.map